import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Refresh,
  Settings,
  Visibility,
  VisibilityOff,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { apiService } from '../../services/api';

// ---------------------------------------------------------------------------
// Color palette — maps schedule version_color → hex
// ---------------------------------------------------------------------------
const SCHEDULE_VERSION_COLORS: Record<string, string> = {
  WHITE:     '#E2E8F0',
  BLUE:      '#4299E1',
  PINK:      '#F687B3',
  YELLOW:    '#ECC94B',
  GREEN:     '#68D391',
  GOLDENROD: '#D4AC0D',
  TAN:       '#C8A97A',
  SALMON:    '#FC8181',
  BUFF:      '#FAF089',
  CHERRY:    '#E53E3E',
  LAVENDER:  '#D6BCFA',
};

const getVersionColor = (colorName: string): string => {
  const key = (colorName || '').toUpperCase().trim();
  return SCHEDULE_VERSION_COLORS[key] || '#A0AEC0';
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ScheduleVersionDay {
  day: number;
  date: string | null;
  dailyPages: number;
  cumulativePages: number;
}

interface ScheduleVersion {
  id: number;
  color: string;
  label: string;
  isBaseline: boolean;
  isActive: boolean;
  versionDate: string | null;
  totalPages: number;
  days: ScheduleVersionDay[];
}

interface TrajectoryResponse {
  history?: Array<{
    day: number;
    date: string;
    dailyPlanned: number;
    cumulativePlanned: number;
    dailyActual: number;
    cumulativeActual: number;
    dailyDrift: number;
    cumulativeDrift: number;
  }>;
  scheduleVersions?: ScheduleVersion[];
  projection?: {
    currentVelocity: number;
    projectedWrapDay: number;
    scheduledWrapDay: number;
  };
  metadata?: {
    totalScriptPages: number;
    totalDays: number;
    currentDay: number;
    productionStartDate: string;
    scheduledWrapDate: string;
  };
}

interface ChartPoint {
  day: number;
  date: string;
  actual: number | null;
  projected: number | null;
  [key: string]: number | string | null; // planned_<id> per version
}

// ---------------------------------------------------------------------------
// Widget props
// ---------------------------------------------------------------------------
interface ProductionVelocityWidgetProps {
  widgetId: string;
  title: string;
  onRefresh?: () => void;
  onConfigure?: () => void;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ProductionVelocityWidget: React.FC<ProductionVelocityWidgetProps> = ({
  widgetId,
  title,
  onRefresh,
  onConfigure,
  isVisible = true,
  onToggleVisibility,
}) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TrajectoryResponse | null>(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDriftTrajectoryData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to load velocity data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching velocity data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await apiService.refreshDriftTrajectory();
      await fetchData();
      onRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error refreshing data');
    } finally {
      setRefreshing(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const velocity          = data?.projection?.currentVelocity || 0;
  const totalDays         = data?.metadata?.totalDays || 19;
  const currentDay        = data?.metadata?.currentDay || 0;
  const projectedWrapDay  = data?.projection?.projectedWrapDay || totalDays;
  const scheduledWrapDay  = data?.projection?.scheduledWrapDay || totalDays;
  const daysVariance      = projectedWrapDay - scheduledWrapDay;
  const scheduleVersions  = data?.scheduleVersions || [];

  // ── Build unified chart data ──────────────────────────────────────────────
  // X-axis: union of all days across all schedule versions + actual days
  const allDayNums = new Set<number>();
  scheduleVersions.forEach(v => v.days.forEach(d => allDayNums.add(d.day)));
  (data?.history || []).forEach(h => allDayNums.add(h.day));
  // Extend x-axis for projection
  for (let d = 1; d <= totalDays + 3; d++) allDayNums.add(d);
  const sortedDays = Array.from(allDayNums).sort((a, b) => a - b);

  // Build a lookup for actuals keyed by day
  const actualsMap: Record<number, number> = {};
  (data?.history || []).forEach(h => { actualsMap[h.day] = h.cumulativeActual; });

  // Identify the last actual day
  const actualDays = Object.keys(actualsMap).map(Number).sort((a, b) => a - b);
  const lastActualDay = actualDays[actualDays.length - 1] || 0;
  const lastActualCumulative = actualsMap[lastActualDay] || 0;

  // Build cumulative actual lookup from history
  const chartData: ChartPoint[] = sortedDays.map(day => {
    const point: ChartPoint = {
      day,
      date: `D${day}`,
      actual: actualsMap[day] !== undefined ? actualsMap[day] : null,
      projected: null,
    };
    // Per-version planned columns
    scheduleVersions.forEach(v => {
      const vDay = v.days.find(d => d.day === day);
      point[`planned_${v.id}`] = vDay ? vDay.cumulativePages : null;
    });
    return point;
  });

  // Extend projected trajectory from last actual day forward
  if (lastActualDay > 0 && velocity > 0) {
    chartData.forEach(point => {
      if (point.day >= lastActualDay) {
        const daysAhead = point.day - lastActualDay;
        point.projected = parseFloat((lastActualCumulative + daysAhead * velocity).toFixed(2));
      }
    });
  }

  // ── Status helpers ────────────────────────────────────────────────────────
  const getStatusColor = () => {
    if (daysVariance < 0) return '#48BB78';
    if (daysVariance === 0) return '#4299E1';
    if (daysVariance <= 2) return '#ECC94B';
    return '#F56565';
  };

  const getStatusIcon = () => {
    if (daysVariance < 0) return <TrendingUp sx={{ color: '#48BB78' }} />;
    if (daysVariance === 0) return <TrendingFlat sx={{ color: '#4299E1' }} />;
    return <TrendingDown sx={{ color: '#F56565' }} />;
  };

  const getStatusLabel = () => {
    if (currentDay === 0) return 'Awaiting DPRs';
    if (daysVariance < 0) return `${Math.abs(daysVariance)}d ahead`;
    if (daysVariance === 0) return 'On Schedule';
    return `${daysVariance}d behind`;
  };

  // Show chart if we have schedule version data even without actuals
  const hasScheduleData = scheduleVersions.some(v => v.days.length > 0);
  const hasActuals = currentDay > 0;

  // ── Custom tooltip ────────────────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{
        background: '#1A202C',
        border: '1px solid #4A5568',
        borderRadius: 1,
        p: 1.5,
        minWidth: 160,
      }}>
        <Typography variant="caption" sx={{ color: '#A0AEC0', display: 'block', mb: 0.5 }}>
          Day {label}
        </Typography>
        {payload.map((entry: any) => (
          entry.value != null && (
            <Box key={entry.dataKey} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ color: entry.color }}>
                {entry.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#E2E8F0', fontWeight: 'bold' }}>
                {Number(entry.value).toFixed(1)} pg
              </Typography>
            </Box>
          )
        ))}
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card sx={{ height: '100%', backgroundColor: '#2D3748', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #4A5568', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
          {(hasScheduleData || hasActuals) && (
            <Chip
              size="small"
              icon={getStatusIcon()}
              label={getStatusLabel()}
              sx={{
                backgroundColor: `${getStatusColor()}22`,
                color: getStatusColor(),
                fontWeight: 'bold',
                '& .MuiChip-icon': { color: getStatusColor() },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={handleRefresh} disabled={refreshing || loading} sx={{ color: '#A0AEC0' }}>
            <Refresh sx={{
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
            }} />
          </IconButton>
          {onConfigure && (
            <IconButton size="small" onClick={onConfigure} sx={{ color: '#A0AEC0' }}>
              <Settings fontSize="small" />
            </IconButton>
          )}
          {onToggleVisibility && (
            <IconButton size="small" onClick={onToggleVisibility} sx={{ color: '#A0AEC0' }}>
              {isVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress size={40} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && !hasScheduleData && (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} sx={{ color: '#A0AEC0' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>No production data yet</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Data will appear once a schedule is processed
            </Typography>
          </Box>
        )}

        {!loading && !error && hasScheduleData && (
          <>
            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {hasActuals ? (
                <>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Current Velocity</Typography>
                    <Typography variant="h6" fontWeight="bold">{velocity.toFixed(2)} pg/day</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Current Day</Typography>
                    <Typography variant="h6" fontWeight="bold">{currentDay} / {totalDays}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Projected Wrap</Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: getStatusColor() }}>
                      Day {projectedWrapDay}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Scheduled Days</Typography>
                    <Typography variant="h6" fontWeight="bold">{totalDays}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Total Pages</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {scheduleVersions.find(v => v.isBaseline)?.totalPages.toFixed(1) || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Schedules</Typography>
                    <Typography variant="h6" fontWeight="bold">{scheduleVersions.length}</Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Schedule version legend pills */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              {scheduleVersions.map(v => (
                <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 24, height: 3,
                    background: getVersionColor(v.color),
                    borderRadius: 1,
                    opacity: 0.9,
                    borderBottom: v.isBaseline ? 'none' : '1px dashed transparent',
                  }} />
                  <Typography variant="caption" sx={{ color: '#A0AEC0', fontSize: 10 }}>
                    {v.isBaseline ? '●' : '○'} {v.color || 'Schedule'}{v.isBaseline ? ' (Baseline)' : ' (Revision)'}
                  </Typography>
                </Box>
              ))}
              {hasActuals && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 24, height: 3, background: '#48BB78', borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ color: '#A0AEC0', fontSize: 10 }}>● Actual</Typography>
                </Box>
              )}
            </Box>

            {/* Chart */}
            <Box sx={{ flex: 1, minHeight: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3D4A5C" />
                  <XAxis
                    dataKey="day"
                    stroke="#A0AEC0"
                    tick={{ fill: '#A0AEC0', fontSize: 11 }}
                    tickFormatter={(v) => `D${v}`}
                  />
                  <YAxis
                    stroke="#A0AEC0"
                    tick={{ fill: '#A0AEC0', fontSize: 11 }}
                    width={40}
                    label={{ value: 'Pages', angle: -90, position: 'insideLeft', fill: '#718096', fontSize: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    height={28}
                    wrapperStyle={{ fontSize: '11px', color: '#A0AEC0' }}
                  />

                  {/* One dashed line per schedule version */}
                  {scheduleVersions.map((v, idx) => {
                    const color = getVersionColor(v.color);
                    const label = v.isBaseline
                      ? `${v.color || 'Schedule'} Baseline`
                      : `${v.color || 'Revision'} Revision`;
                    return (
                      <Line
                        key={`planned_${v.id}`}
                        type="monotone"
                        dataKey={`planned_${v.id}`}
                        name={label}
                        stroke={color}
                        strokeWidth={v.isBaseline ? 2 : 1.5}
                        strokeDasharray={v.isBaseline ? '6 3' : '3 3'}
                        dot={false}
                        connectNulls
                        opacity={0.85}
                      />
                    );
                  })}

                  {/* Actual progress */}
                  {hasActuals && (
                    <Line
                      type="monotone"
                      dataKey="actual"
                      name="Actual"
                      stroke="#48BB78"
                      strokeWidth={3}
                      dot={{ fill: '#48BB78', r: 3 }}
                      connectNulls={false}
                      opacity={1}
                    />
                  )}

                  {/* Projected trajectory */}
                  {hasActuals && velocity > 0 && (
                    <Line
                      type="monotone"
                      dataKey="projected"
                      name="Projected"
                      stroke="#ED8936"
                      strokeWidth={2}
                      strokeDasharray="3 2"
                      dot={false}
                      connectNulls={false}
                      opacity={0.8}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {/* No actuals hint */}
            {!hasActuals && (
              <Typography variant="caption" sx={{ color: '#4A5568', textAlign: 'center', mt: 1 }}>
                Actuals and projection will appear once DPRs are processed
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductionVelocityWidget;
